import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { OptionCard } from 'components/OptionCard'
import { SvgImage } from 'components/SvgImage'
import {
  TERMS_LINKS_GROUP_VARIANT,
  TermsLinksGroup,
} from 'components/TermsLinksGroup'

import { useCohortInfo } from 'hooks/useCohortInfo'
import { useDelayedContinue } from 'hooks/useDelayedContinue'
import { useNextStep } from 'hooks/useNextStep'

import { TPageProps } from 'models/common.model'

import starSvg from 'assets/images/sprite/star-gold.svg'

import { CustomPageId } from 'page-constants'
import { Cohort, Language, OptionType } from 'root-constants'

import {
  StyledSelectGenderV2 as S,
  SEPARATOR_VARIANT,
} from './SelectGenderV2.styles'
import {
  CONTENT_CONFIG,
  GENDER_OPTIONS_MAP,
  VERSION_BY_COHORT,
} from './constants'

export const SelectGenderV2: React.FC<TPageProps> = ({
  pageId,
  nextPagePath,
  isFirstPage,
}) => {
  const { t } = useTranslation()
  const { isAnswersDisabled, handleChange, setIsAnswersDisabled } =
    useDelayedContinue()
  const { cohortToUse } = useCohortInfo()

  const [answer, setAnswer] = useState<string>('')

  const context = VERSION_BY_COHORT[cohortToUse] || VERSION_BY_COHORT.default
  const genderOptions = GENDER_OPTIONS_MAP[context]
  const isPrintableCohort = cohortToUse === Cohort.FLOW_1_INTRO_LONG_4412

  const { showSubtitle, showTopic, isLargeTitle } = CONTENT_CONFIG[context]

  const handleContinue = useNextStep({
    pageId: CustomPageId.GENDER,
    question: t('onboarding.selectGenderV2.title', {
      lng: Language.EN,
      context,
    }),
    nextPagePath,
    isFirstPage,
  })

  const optionProps = {
    name: pageId,
    type: OptionType.RADIO,
    onChange: (value: string) => {
      handleChange(value, handleContinue)
      setIsAnswersDisabled(true)
      setAnswer(value)
    },
  }

  return (
    <S.Container>
      {!context && (
        <S.RatingContainer>
          <SvgImage svg={starSvg} width={14} height={14} />
          <S.RatingText>{t('onboarding.selectGenderV2.rating')}</S.RatingText>
          <S.Separator variant={SEPARATOR_VARIANT.RATING} />
          <S.RatingText>
            {t('onboarding.selectGenderV2.ratingReviews')}
          </S.RatingText>
        </S.RatingContainer>
      )}

      <S.Title $isLarge={isLargeTitle}>
        {t('onboarding.selectGenderV2.title', { context })}
      </S.Title>

      {showSubtitle && (
        <S.Subtitle>
          {t('onboarding.selectGenderV2.subtitle', { context })}
        </S.Subtitle>
      )}

      {showTopic && (
        <S.Topic>
          {t('onboarding.selectGenderV2.selectTitle', { context })}
        </S.Topic>
      )}

      <S.CardsContainer>
        {genderOptions.map(({ value, text, imageUrl }) => (
          <OptionCard
            key={value}
            imageUrl={imageUrl}
            value={value}
            label={t(text)}
            checked={answer === value}
            disabled={isAnswersDisabled}
            {...optionProps}
          />
        ))}
      </S.CardsContainer>

      {!isPrintableCohort && (
        <>
          <TermsLinksGroup
            titleKey="commonComponents.termsGenderTitle"
            variant={TERMS_LINKS_GROUP_VARIANT.DARK}
          />

          <S.TermsNote>{t('commonComponents.termsNote')}</S.TermsNote>
        </>
      )}
    </S.Container>
  )
}
